/// <amd-module name="Wit/MentionsAutocomplete/WorkItemAutocompleteProvider" />
import * as React from "react";
import * as ReactDOM from "react-dom";
import { IAutocompleteProvider, ISuggestionSearchResult, ISuggestionSearchResultItem } from "Mention/Autocomplete/Contracts";
import { ObservableValue } from "VSS/Core/Observable";
import { IMarkdownTransformContext } from "VSS/Features/Markdown/MarkdownRendererContracts";
import { IMenuService } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext, Services, VssService } from "VSS/Platform/Context";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { IVssLocationService } from "VSS/Platform/Location";
import { IVssTelemetryService } from "VSS/Platform/Telemetry";
import { IMenuItem } from "VSSUI/Menu";
import { Tooltip } from "VSSUI/TooltipEx";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { WorkItemTrackingDataProvider, WorkItemTypeIconProvider } from "Wit/UI/Data";
import { WorkItemTypeIcon } from "Wit/UI/WorkItemTypeIcon";
import { filterXSS } from "xss";
import { xssStyleDefaultOptions } from "xss/PresetOptions";
import { IWorkItemProviderItem } from "./Providers/IWorkItemProviderItem";
import { ISearchResultWorkItem, WorkItemMentionDataProvider } from "./Providers/WorkItemMentionDataProvider";
import { WorkItemAutocompleteCommand } from "./WorkItemAutocompleteCommand";
import { WorkItemMentionPreview } from "./WorkItemMentionPreview";

const PATTERN_WORD_START_SEPARATOR = "\\s|^|-|\\.|,|:|;|'|\"|\\(";
const PATTERN_WORD_END_SEPARATOR = "\\s|$|\\.|,|:|;|-|' | \"|\\)";
const matchRegex = new RegExp(`(${PATTERN_WORD_START_SEPARATOR})#([0-9]{1,10})(?=(${PATTERN_WORD_END_SEPARATOR}))`, "ig");
const witMentionRegex = /^#\d+$/;

interface IWorkItem {
    id: string;
    title: string;
    workItemType: string;
    projectName: string;
}

class WorkItemAutocompleteProviderService extends VssService implements IAutocompleteProvider, IMenuService {
    private _mentionDataProvider: WorkItemMentionDataProvider;
    private _workItemTrackingDataProvider: WorkItemTrackingDataProvider;

    public readonly shortcutCharacter = "#";

    public _serviceStart(pageContext: IVssPageContext) {
        super._serviceStart(pageContext);
        const useRestWISearch = isFeatureFlagEnabled(pageContext, FeatureFlags.RestMentionSearch, false)

        this._mentionDataProvider = new WorkItemMentionDataProvider(pageContext, { useRestSearch: useRestWISearch });
        this._workItemTrackingDataProvider = new WorkItemTrackingDataProvider(pageContext);
    }

    public getMenuItems(): IMenuItem[] {
        return [new WorkItemAutocompleteCommand()];
    }

    public getSuggestions(): Promise<ISuggestionSearchResult<IWorkItem>[]> {
        return this._getSuggestions("", false);
    }

    public search(term: string, isSearchEnabled: boolean): Promise<ISuggestionSearchResultItem[]> {
        return this._getSuggestions(term, isSearchEnabled);
    }

    /**
     * Given a Work item mention, this will fetch the work item to get its title
     * and then render the mention so that text content like #123 will become a nice looking Work item link
     */
    public transformMarkdown(transformContext: IMarkdownTransformContext, ignoreMatch?: (matchIndex: number) => boolean): void {
        transformContext.markup = transformContext.markup.replace(
            matchRegex,
            (fullMatch: string, wordStart: string, workItemId: string, wordEnd: string, offset: number) => {
                if (ignoreMatch && ignoreMatch(offset)) {
                    return fullMatch;
                }

                const workItemMention = new ObservableValue<IWorkItemProviderItem | undefined>(undefined);
                this._mentionDataProvider.getByIdAsync(workItemId).then(w => (workItemMention.value = w));

                return transformContext.createReplacement(
                    <WorkItemMentionPreview
                        pageContext={this.pageContext}
                        workItemId={workItemId}
                        workItemMention={workItemMention}
                        dataProvider={this._workItemTrackingDataProvider}
                        onMentionClick={mention => this.onMentionClick(mention)}
                    />,
                    "span"
                );
            }
        );
    }

    /**
     * Given an html document containing mentions as raw anchor tags, this function will replace those mentions
     * with nice-looing work item links.
     */
    public async transformHtml(element: HTMLElement): Promise<void> {
        await Promise.all(
            Array.from(element.querySelectorAll("a[data-vss-mention]")).map(async (a: HTMLAnchorElement) => {
                const linkContents = a.innerText;
                if (!witMentionRegex.test(linkContents)) {
                    return;
                }

                const workItemId = linkContents.substr(1);
                const workItemMention = await this._mentionDataProvider.getByIdAsync(workItemId);

                if (workItemMention) {
                    // Because the rendered HTML is going to be converted to a string, we need
                    // to make sure icon and state data are resolved before we render.
                    await Promise.resolve(
                        this._workItemTrackingDataProvider.getWorkItemTypeColorAndIcon(workItemMention.projectName, workItemMention.workItemType)
                    );
                    await Promise.resolve(
                        this._workItemTrackingDataProvider.getWorkItemStateAndColor(
                            workItemMention.projectName,
                            workItemMention.workItemType,
                            workItemMention.state
                        )
                    );

                    const replacement = (
                        <WorkItemMentionPreview
                            pageContext={this.pageContext}
                            workItemId={workItemId}
                            workItemMention={workItemMention}
                            dataProvider={this._workItemTrackingDataProvider}
                            onMentionClick={mention => this.onMentionClick(mention)}
                        />
                    );

                    const newRoot = document.createElement("span");
                    a.replaceWith(newRoot);
                    ReactDOM.render(replacement, newRoot);
                }
            })
        );
    }

    public renderSuggestion(item: ISuggestionSearchResult<IWorkItem>): JSX.Element {
        const { original, highlighted } = item;
        const workItemTypeName = original.workItemType;
        const projectIdOrName = original.projectName;

        const typeAndIdHtml = {
            __html: filterXSS(
                highlighted ? `${highlighted.workItemType} ${highlighted.id}` : `${original.workItemType} ${original.id}`,
                xssStyleDefaultOptions
            )
        };

        const titleHtml = {
            __html: filterXSS(highlighted ? highlighted.title : original.title, xssStyleDefaultOptions)
        };

        return (
            <div className="flex-center flex-row">
                <WorkItemTypeIconProvider dataProvider={this._workItemTrackingDataProvider} dataSource={{ projectIdOrName, workItemTypeName }}>
                    {icon => <WorkItemTypeIcon data={icon} className="work-item-icon" />}
                </WorkItemTypeIconProvider>
                <span className="work-item-type-and-id" dangerouslySetInnerHTML={typeAndIdHtml} />
                <span className="content-splitter">: </span>
                <Tooltip text={original.title}>
                    <span className="work-item-title" dangerouslySetInnerHTML={titleHtml} />
                </Tooltip>
            </div>
        );
    }

    public getReplacementText(item: ISuggestionSearchResult<IWorkItem>): string {
        return `${this.shortcutCharacter}${item.original.id}`;
    }

    public getReplacementHtml(item: ISuggestionSearchResult<IWorkItem>): string {
        const locationService = this.pageContext.getService<IVssLocationService>("IVssLocationService");
        const mentionUrl = locationService.routeAbsoluteUrl("ms.vss-work-web.work-items-form-route", {
            id: `${item.original.id}`,
            project: item.original.projectName
        });
        return `<a href="${mentionUrl}" data-vss-mention="version:1.0">${this.shortcutCharacter}${item.original.id}</a>`;
    }

    private onMentionClick(workItem: IWorkItemProviderItem) {
        const telemetryService = this.pageContext.getService<IVssTelemetryService>("IVssTelemetryService");
        telemetryService.publishEvent("WIT", "WorkItemMentionClick", {
            workitem: workItem
        });
    }

    private _getSuggestions(inputText: string, isSearchOnServer?: boolean): Promise<ISuggestionSearchResult<IWorkItem>[]> {
        return new Promise<ISuggestionSearchResultItem[]>((resolve, reject) => {
            if (WorkItemMentionDataProvider.getTerms(inputText).termsOverLimit) {
                resolve([]);
                return;
            }

            let searchResultPromise: PromiseLike<ISearchResultWorkItem[]>;
            inputText = inputText.trim();
            const numberMatches = inputText.match(/^(\d+)$/);

            if (isSearchOnServer && inputText.length > 0) {
                searchResultPromise = this._mentionDataProvider.searchOnServerAsync(inputText);
            } else if (numberMatches) {
                searchResultPromise = this._mentionDataProvider.searchByIdAsync(Number(inputText));
            } else {
                searchResultPromise = this._mentionDataProvider.searchAsync(inputText);
            }

            searchResultPromise.then((workItems: ISearchResultWorkItem[]) => {
                if (!workItems) {
                    // Telemetry.traceError(this.context.pageContext, "WorkItemAutocomplete", "Search suggestions result is null.", {
                    //     searchMethod
                    // });
                    resolve([]);
                    return;
                }

                const result = workItems.map((item: ISearchResultWorkItem) => {
                    return {
                        original: {
                            id: item.original.id.toString(),
                            title: item.original.title,
                            workItemType: item.original.workItemType,
                            projectName: item.original.projectName
                        } as IWorkItem,
                        highlighted: item.highlighted
                            ? ({
                                  id: item.highlighted.id,
                                  title: item.highlighted.title,
                                  workItemType: item.highlighted.workItemType,
                                  projectName: item.original.projectName
                              } as IWorkItem)
                            : null
                    } as ISuggestionSearchResult<IWorkItem>;
                });

                resolve(result);
            }, reject);
        });
    }
}

Services.add("work-item-autocomplete-service", { serviceFactory: WorkItemAutocompleteProviderService });
